import React, { useState, useEffect, useRef } from 'react';
import './NumberPicker.css';

interface NumberPickerProps {
  value: string;
  onChange: (value: string) => void;
  minValue?: number;
  maxValue?: number;
  placeholder?: string;
  initialOpen?: boolean;
}

const NumberPicker: React.FC<NumberPickerProps> = ({
  value,
  onChange,
  minValue = 1,
  maxValue = 99,
  placeholder = 'Select a number',
  initialOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const pickerRef = useRef<HTMLDivElement>(null);
  
  // Parse the numeric value
  const getNumericValue = (val: string): number => {
    if (!val) return 0;
    const num = parseInt(val, 10);
    return isNaN(num) ? 0 : num;
  };

  const currentValue = getNumericValue(value);

  useEffect(() => {
    // Close the picker if user clicks outside
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNumberInput = (num: number) => {
    let newValue: string;
    
    if (currentValue === 0) {
      newValue = num.toString();
    } else {
      const combined = parseInt(`${currentValue}${num}`, 10);
      if (combined <= maxValue) {
        newValue = combined.toString();
      } else {
        newValue = num.toString();
      }
    }
    
    onChange(newValue);
  };

  const handleClear = () => {
    onChange('');
  };

  const handleBackspace = () => {
    if (value.length > 0) {
      const newValue = value.slice(0, -1);
      onChange(newValue);
    }
  };

  // Number pad layout
  const numberPad = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    ['clear', 0, 'backspace']
  ];

  // Check if a single digit is selected (for highlighting the button)
  const isSingleDigit = (value: string): boolean => {
    return value.length === 1 && !isNaN(parseInt(value, 10));
  };

  return (
    <>
      {!initialOpen ? (
        <div className="number-picker-container" ref={pickerRef}>
          <div 
            className="number-picker-display" 
            onClick={() => setIsOpen(!isOpen)}
          >
            {value || placeholder}
          </div>
          
          {isOpen && (
            <div className="number-picker-numpad">
              {numberPad.map((row, rowIndex) => (
                <div key={`row-${rowIndex}`} className="number-picker-row">
                  {row.map((item, colIndex) => {
                    if (item === 'clear') {
                      return (
                        <button
                          key={`col-${colIndex}`}
                          className="number-picker-key function-key clear-key"
                          onClick={handleClear}
                          aria-label="Clear"
                          type="button"
                        >
                          C
                        </button>
                      );
                    } else if (item === 'backspace') {
                      return (
                        <button
                          key={`col-${colIndex}`}
                          className="number-picker-key function-key backspace-key"
                          onClick={handleBackspace}
                          aria-label="Backspace"
                          type="button"
                        >
                          ←
                        </button>
                      );
                    } else {
                      const isSelected = isSingleDigit(value) && parseInt(value, 10) === item;
                      return (
                        <button
                          key={`col-${colIndex}`}
                          className={`number-picker-key ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleNumberInput(item as number)}
                          aria-label={`Number ${item}`}
                          type="button"
                        >
                          {item}
                        </button>
                      );
                    }
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="number-picker-numpad-standalone">
          <div className="number-picker-value-display">
            {value || placeholder}
          </div>
          {numberPad.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="number-picker-row">
              {row.map((item, colIndex) => {
                if (item === 'clear') {
                  return (
                    <button
                      key={`col-${colIndex}`}
                      className="number-picker-key function-key clear-key"
                      onClick={handleClear}
                      aria-label="Clear"
                      type="button"
                    >
                      C
                    </button>
                  );
                } else if (item === 'backspace') {
                  return (
                    <button
                      key={`col-${colIndex}`}
                      className="number-picker-key function-key backspace-key"
                      onClick={handleBackspace}
                      aria-label="Backspace"
                      type="button"
                    >
                      ←
                    </button>
                  );
                } else {
                  const isSelected = isSingleDigit(value) && parseInt(value, 10) === item;
                  return (
                    <button
                      key={`col-${colIndex}`}
                      className={`number-picker-key ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleNumberInput(item as number)}
                      aria-label={`Number ${item}`}
                      type="button"
                    >
                      {item}
                    </button>
                  );
                }
              })}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default NumberPicker;
